import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Search, Loader2, CheckCircle2, AlertTriangle, XCircle,
  ArrowRight, Sparkles, TrendingUp, Building2, Phone, Globe,
} from "lucide-react";
import { lvRunAudit } from "@/lib/api";
import { toast } from "sonner";
import InfographicHero from "@/components/InfographicHero";

/** Local Visibility Engine — public audit page (`/local-visibility`). */

const scoreTone = (v) => (v >= 75 ? "emerald" : v >= 50 ? "amber" : "rose");
const TONE = {
  emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
  amber: "text-amber-700 bg-amber-50 border-amber-200",
  rose: "text-rose-700 bg-rose-50 border-rose-200",
};
const SEV = {
  critical: "border-rose-200 bg-rose-50 text-rose-700",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  info: "border-slate-200 bg-slate-50 text-slate-700",
};

export default function LocalVisibilityLanding() {
  const [form, setForm] = useState({
    business_name: "", address: "", phone: "", website: "", category: "",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Local Visibility Engine — WebsiteTech.pro";
  }, []);

  const run = async () => {
    if (!form.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setLoading(true);
    try {
      const r = await lvRunAudit(form);
      setResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed");
    } finally { setLoading(false); }
  };

  const tone = result ? scoreTone(result.score) : "amber";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="lv-landing">
      <Navbar />

      <section className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-orange-600">
            <MapPin className="h-4 w-4" /> WebsiteTech · Local Visibility Engine
          </div>
          <h1 className="mt-4 font-heading text-4xl font-black leading-tight text-slate-900 sm:text-5xl lg:text-6xl">
            One score for <span className="text-orange-600">how findable</span> your local business really is.
          </h1>
          <p className="mt-6 max-w-2xl text-base text-slate-600 sm:text-lg">
            We check the eight signals that decide whether Google Maps, Apple
            Maps, and AI answer engines send you customers — and tell you the
            estimated leads you're losing when they don't.
          </p>

          <div className="mt-8">
            <InfographicHero slug="local-visibility-engine" hint={false} />
          </div>

          <Card className="mt-8 border-slate-200 bg-slate-50 p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Business name*" value={form.business_name}
                onChange={(v) => setForm({ ...form, business_name: v })}
                icon={<Building2 className="h-4 w-4 text-slate-400" />}
                testid="lv-name" />
              <Field label="Category" value={form.category}
                onChange={(v) => setForm({ ...form, category: v })}
                icon={<Sparkles className="h-4 w-4 text-slate-400" />}
                testid="lv-category" />
              <Field label="Address" value={form.address}
                onChange={(v) => setForm({ ...form, address: v })}
                icon={<MapPin className="h-4 w-4 text-slate-400" />}
                testid="lv-address" />
              <Field label="Phone" value={form.phone}
                onChange={(v) => setForm({ ...form, phone: v })}
                icon={<Phone className="h-4 w-4 text-slate-400" />}
                testid="lv-phone" />
              <Field label="Website" value={form.website}
                onChange={(v) => setForm({ ...form, website: v })}
                icon={<Globe className="h-4 w-4 text-slate-400" />}
                testid="lv-website" />
              <div className="flex items-end">
                <Button
                  onClick={run}
                  disabled={loading || !form.business_name.trim()}
                  className="w-full bg-orange-600 text-white hover:bg-orange-700"
                  data-testid="lv-run"
                >
                  {loading
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Search className="mr-2 h-4 w-4" />}
                  Run Local Visibility check
                </Button>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Phase 1 heuristic · Data Confidence: <b>Low</b>.
              Connect Google Business Profile to lift confidence to High.
            </div>
          </Card>
        </div>
      </section>

      {result && (
        <section className="mx-auto max-w-6xl px-4 py-12" data-testid="lv-result">
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className={`border p-6 ${TONE[tone]}`}>
              <div className="text-xs font-semibold uppercase opacity-70">
                Local Visibility Score
              </div>
              <div className="mt-2 font-heading text-6xl font-black tabular-nums">
                {result.score}
                <span className="text-2xl opacity-60">/100</span>
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm">
                <TrendingUp className="h-4 w-4" />
                Est. missed leads / month: <b>{result.estimated_missed_leads_per_month}</b>
              </div>
              <div className="mt-4 text-xs opacity-70">
                {result.confidence}
              </div>
            </Card>

            <Card className="border-slate-200 p-6 lg:col-span-2">
              <div className="font-heading text-lg font-bold text-slate-900">
                Findings ({result.findings.length}) &amp; opportunities ({result.opportunities.length})
              </div>
              <div className="mt-4 space-y-2">
                {result.findings.map((f) => (
                  <div key={f.key}
                    className={`rounded-xl border px-4 py-3 ${SEV[f.severity]}`}
                    data-testid={`lv-finding-${f.key}`}>
                    <div className="flex items-start gap-3">
                      {f.severity === "critical" ? <XCircle className="mt-0.5 h-4 w-4" />
                        : <AlertTriangle className="mt-0.5 h-4 w-4" />}
                      <div>
                        <div className="text-sm font-semibold">{f.label}</div>
                        <div className="mt-0.5 text-xs opacity-80">{f.hint}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {result.opportunities.map((f) => (
                  <div key={f.key}
                    className={`rounded-xl border px-4 py-3 ${SEV.info}`}
                    data-testid={`lv-opp-${f.key}`}>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 text-sky-600" />
                      <div>
                        <div className="text-sm font-semibold">{f.label}</div>
                        <div className="mt-0.5 text-xs opacity-80">{f.hint}</div>
                      </div>
                    </div>
                  </div>
                ))}
                {result.findings.length === 0 && result.opportunities.length === 0 && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    Every checked signal passed. Excellent local visibility base.
                  </div>
                )}
              </div>
              <div className="mt-6 border-t border-slate-200 pt-4 text-xs text-slate-500">
                Eight signals checked: NAP consistency, GBP claim, category,
                Apple Business Connect, Bing Places, 20+ citations, review
                velocity, website + schema markup.
              </div>
            </Card>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-700"
              data-testid="lv-cta-packages"
            >
              See how Local Growth plan fixes this <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/marketing-manager"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              Ask the AI Marketing Manager
            </Link>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}

function Field({ label, value, onChange, icon, testid }) {
  return (
    <label className="text-xs font-semibold text-slate-700">
      {label}
      <div className="relative mt-1">
        {icon && <div className="absolute left-3 top-1/2 -translate-y-1/2">{icon}</div>}
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`bg-white ${icon ? "pl-9" : ""}`}
          data-testid={testid}
        />
      </div>
    </label>
  );
}
